import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import Swal from "sweetalert2";
import { lojaService } from "../../../../services/lojaService";
import { metaService } from "../../../../services/metaService";
import { optionsService } from "../../../../services/optionsService";
import { MetaCreateData } from "../../../../types/meta";
import { toast } from "react-toastify";
import { COLORS, MESSAGES } from "../../../../constants";

interface FilterState {
  name: string;
  lojaId: string;
  tipo: string;
}

interface MetaIndividualContextType {
  filters: FilterState;
  searchFilters: FilterState;
  lojas: Array<{ id: number; nome: string }>;
  usuarios: Array<{ id: number; nome: string }>;
  handleFieldChange: (name: string, value: string) => void;
  handleSearch: () => void;
  handleClear: () => void;
  isOpen: boolean;
  editingItem: any;
  openModal: (item?: any) => void;
  closeModal: () => void;
  metas: any[];
  isLoading: boolean;
  handleSubmit: (formData: MetaCreateData) => Promise<void>;
  handleEdit: (rowIndex: number) => void;
  handleDelete: (rowIndex: number) => Promise<void>;
}

const MetaIndividualContext = createContext<
  MetaIndividualContextType | undefined
>(undefined);

export const MetaIndividualProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [filters, setFilters] = useState<FilterState>({
    name: "",
    lojaId: "",
    tipo: "",
  });

  const [searchFilters, setSearchFilters] = useState<FilterState>({
    name: "",
    lojaId: "",
    tipo: "",
  });

  const [lojas, setLojas] = useState<Array<{ id: number; nome: string }>>([]);
  const [usuarios, setUsuarios] = useState<Array<{ id: number; nome: string }>>(
    []
  );
  const [metas, setMetas] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchLojas = useCallback(async () => {
    try {
      const data = await lojaService.getLojas();
      setLojas(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Erro ao carregar lojas");
    }
  }, []);

  const fetchUsuarios = useCallback(async () => {
    try {
      const data = await optionsService.getUsuarios();
      setUsuarios(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Erro ao carregar usuários");
    }
  }, []);

  const fetchMetas = useCallback(async () => {
    setIsLoading(true);
    try {
      const filtersToSend = {
        name: searchFilters.name || undefined,
        lojaId: searchFilters.lojaId
          ? parseInt(searchFilters.lojaId, 10)
          : undefined,
        tipo: searchFilters.tipo as "producao" | "entrega" | undefined,
      };
      const data = await metaService.getMetas(filtersToSend);
      setMetas(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Erro ao carregar metas");
    } finally {
      setIsLoading(false);
    }
  }, [searchFilters.name, searchFilters.lojaId, searchFilters.tipo]);

  useEffect(() => {
    fetchLojas();
    fetchUsuarios();
  }, [fetchLojas, fetchUsuarios]);

  useEffect(() => {
    fetchMetas();
  }, [fetchMetas]);

  const openModal = (item?: any) => {
    setEditingItem(item ?? null);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setEditingItem(null);
  };

  const handleFieldChange = (name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    setSearchFilters({ ...filters });
  };

  const handleClear = () => {
    setFilters({ name: "", lojaId: "", tipo: "" });
    setSearchFilters({ name: "", lojaId: "", tipo: "" });
  };

  const handleSubmit = async (formData: MetaCreateData) => {
    setIsLoading(true);
    try {
      if (editingItem) {
        await metaService.updateMeta(editingItem.id, formData);
        toast.success(MESSAGES.UPDATE);
      } else {
        await metaService.createMeta(formData);
        toast.success(MESSAGES.CREATE);
      }
      closeModal();
      await fetchMetas();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Erro ao salvar meta";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (rowIndex: number) => {
    openModal(metas[rowIndex]);
  };

  const handleDelete = async (rowIndex: number) => {
    const result = await Swal.fire({
      title: "Tem certeza?",
      text: "Esta ação não pode ser desfeita",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: COLORS.PRIMARY,
      cancelButtonColor: COLORS.NEUTRAL_500,
      confirmButtonText: "Sim, deletar!",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await metaService.deleteMeta(metas[rowIndex].id);
        Swal.fire({
          title: "Deletado!",
          text: "A meta foi removida com sucesso.",
          icon: "success",
          confirmButtonColor: COLORS.PRIMARY,
        });
        await fetchMetas();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Erro ao excluir meta";
        toast.error(message);
      }
    }
  };

  return (
    <MetaIndividualContext.Provider
      value={{
        filters,
        searchFilters,
        lojas,
        usuarios,
        handleFieldChange,
        handleSearch,
        handleClear,
        isOpen,
        editingItem,
        openModal,
        closeModal,
        metas,
        isLoading,
        handleSubmit,
        handleEdit,
        handleDelete,
      }}
    >
      {children}
    </MetaIndividualContext.Provider>
  );
};

export const useMetaIndividual = () => {
  const context = useContext(MetaIndividualContext);
  if (!context) {
    throw new Error(
      "useMetaIndividual must be used within MetaIndividualProvider"
    );
  }
  return context;
};
